import "./NotificationCenterModal.css"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
    readAllNotifications,
    unreadAllNotifications,
    clearAllNotifications,
    readNotification,
    unreadNotification,
    clearNotification,
} from "../services/chaperonConnector"
import {
    notificationActionLabel,
    notificationActionPath,
} from "../utilities/notifications"

export default function NotificationCenterModal({
    notifications = [],
    isOpen = false,
    onClose,
    userId,
    onNotificationsUpdated,
}) {
    const navigate = useNavigate()

    // Handle Escape key to close modal
    useEffect(() => {
        const handleEscapeKey = (event) => {
            if (event.key === "Escape" && isOpen) {
                onClose()
            }
        }

        if (isOpen) {
            document.addEventListener("keydown", handleEscapeKey)
        }

        return () => {
            document.removeEventListener("keydown", handleEscapeKey)
        }
    }, [isOpen, onClose])

    // Track which notifications are expanded
    const [expandedIds, setExpandedIds] = useState(new Set())

    // Initialize expandedIds with all pending and unread notification IDs
    useEffect(() => {
        const pendingIds = new Set(
            filteredNotifications
                .filter((n) => n.status === "pending" || n.status === "unread")
                .map((n) => n.id),
        )
        setExpandedIds(pendingIds)
    }, [notifications])

    const toggleExpanded = (id) => {
        const newExpandedIds = new Set(expandedIds)
        if (newExpandedIds.has(id)) {
            newExpandedIds.delete(id)
        } else {
            newExpandedIds.add(id)
        }
        setExpandedIds(newExpandedIds)
    }

    const [isProcessing, setIsProcessing] = useState(false)
    const [hoveredNotificationId, setHoveredNotificationId] = useState(null)
    const [hoveredActionButton, setHoveredActionButton] = useState(null)

    const handleMarkAllAsRead = async () => {
        if (!userId || isProcessing) return

        setIsProcessing(true)
        try {
            const response = await readAllNotifications(userId)
            if (response.success) {
                // Collapse all notifications after marking as read
                setExpandedIds(new Set())
                // Refresh notifications list
                if (onNotificationsUpdated) {
                    await onNotificationsUpdated()
                }
            }
        } catch (error) {
            console.error("Error marking all as read:", error)
        } finally {
            setIsProcessing(false)
        }
    }

    const handleMarkAllAsUnread = async () => {
        if (!userId || isProcessing) return

        setIsProcessing(true)
        try {
            const response = await unreadAllNotifications(userId)
            if (response.success) {
                // Expand pending and unread notifications after marking as unread
                const pendingIds = new Set(
                    sortedNotifications
                        .filter(
                            (n) =>
                                n.status === "pending" || n.status === "unread",
                        )
                        .map((n) => n.id),
                )
                setExpandedIds(pendingIds)
                // Refresh notifications list
                if (onNotificationsUpdated) {
                    await onNotificationsUpdated()
                }
            }
        } catch (error) {
            console.error("Error marking all as unread:", error)
        } finally {
            setIsProcessing(false)
        }
    }

    const handleClearAll = async () => {
        if (!userId || isProcessing) return

        setIsProcessing(true)
        try {
            const response = await clearAllNotifications(userId)
            if (response.success) {
                // Refresh notifications list - cleared notifications will be removed
                if (onNotificationsUpdated) {
                    await onNotificationsUpdated()
                }
            }
        } catch (error) {
            console.error("Error clearing all notifications:", error)
        } finally {
            setIsProcessing(false)
        }
    }

    const handleMarkSingleAsRead = async (notificationId) => {
        if (!userId) return

        try {
            const response = await readNotification(userId, notificationId)
            if (response.success) {
                // Refresh notifications list to update status
                if (onNotificationsUpdated) {
                    await onNotificationsUpdated()
                }
            }
        } catch (error) {
            console.error("Error marking notification as read:", error)
        }
    }

    const handleMarkSingleAsUnread = async (notificationId) => {
        if (!userId) return

        try {
            const response = await unreadNotification(userId, notificationId)
            if (response.success) {
                // Refresh notifications list to update status
                if (onNotificationsUpdated) {
                    await onNotificationsUpdated()
                }
            }
        } catch (error) {
            console.error("Error marking notification as unread:", error)
        }
    }

    const handleClearSingleNotification = async (notificationId) => {
        if (!userId) return

        try {
            const response = await clearNotification(userId, notificationId)
            if (response.success) {
                // Refresh notifications list - cleared notification will be removed
                if (onNotificationsUpdated) {
                    await onNotificationsUpdated()
                }
            }
        } catch (error) {
            console.error("Error clearing notification:", error)
        }
    }

    // The CTA button below each notification, same labeling/routing as the
    // dashboard's notifications card. Acting on it implies reading it; when
    // there's nowhere to navigate (no action_type, or metadata missing what
    // the destination needs) this is the only effect, same as the "mark as
    // read" hover button.
    const handleNotificationAction = (notification) => {
        const isUnread =
            notification.status === "pending" ||
            notification.status === "unread"
        if (isUnread) {
            handleMarkSingleAsRead(notification.id)
        }

        const path = notificationActionPath(notification)
        if (path) {
            onClose()
            navigate(path)
        }
    }

    // Filter to only in_app channel with pending, unread, or read status
    const filteredNotifications = notifications.filter(
        (n) =>
            n.channel === "in_app" &&
            (n.status === "pending" ||
                n.status === "unread" ||
                n.status === "read"),
    )

    // Newest first, regardless of status
    const sortedNotifications = [...filteredNotifications].sort(
        (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0),
    )

    const getTypeColor = (type) => {
        switch (type) {
            case "danger":
                return "danger"
            case "warning":
                return "warning"
            case "info":
                return "info"
            case "success":
                return "success"
            default:
                return "secondary"
        }
    }

    const getTypeIcon = (type) => {
        switch (type) {
            case "danger":
                return "bi-exclamation-circle-fill"
            case "warning":
                return "bi-exclamation-triangle-fill"
            case "info":
                return "bi-info-circle-fill"
            case "success":
                return "bi-check-circle-fill"
            default:
                return "bi-bell-fill"
        }
    }

    const truncateTitle = (title, maxLength = 45) => {
        if (!title) return "Notification"
        if (title.length > maxLength) {
            return title.substring(0, maxLength) + "..."
        }
        return title
    }

    return (
        <dialog
            id="notificationCenterModal"
            style={{
                width: "50%",
                minWidth: "400px",
                maxWidth: "600px",
                zIndex: 9999,
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                borderRadius: "12px",
                boxShadow: "0 10px 40px rgba(0, 0, 0, 0.2)",
                border: "none",
                padding: "0",
            }}
            open={isOpen}
        >
            {/* Modal Header */}
            <div
                className="modal-header border-bottom"
                style={{
                    padding: "1.5rem",
                    backgroundColor: "#f8fafc",
                    borderRadius: "12px 12px 0 0",
                }}
            >
                <div className="d-flex align-items-center gap-2">
                    <i
                        className="bi bi-bell-fill text-primary"
                        style={{ fontSize: "1.25rem" }}
                    ></i>
                    <h5 className="mb-0 fw-bold text-dark">Notifications</h5>
                </div>
            </div>

            {/* Modal Body */}
            <div
                className="modal-body"
                style={{
                    padding: "1.5rem",
                    minHeight: "200px",
                    maxHeight: "500px",
                    overflowY: "auto",
                }}
            >
                {sortedNotifications.length === 0 ? (
                    <div className="text-center py-5">
                        <div className="mb-3">
                            <i
                                className="bi bi-bell-slash text-muted"
                                style={{ fontSize: "2.5rem" }}
                            ></i>
                        </div>
                        <p className="text-muted small">
                            No notifications at this time
                        </p>
                    </div>
                ) : (
                    <div>
                        {sortedNotifications.map((notification, index) => (
                            <div
                                key={notification.id}
                                className={`d-flex align-items-start p-3 mb-2 rounded-lg notification-item`}
                                role="alert"
                                onMouseEnter={() =>
                                    setHoveredNotificationId(notification.id)
                                }
                                onMouseLeave={() =>
                                    setHoveredNotificationId(null)
                                }
                                style={{
                                    backgroundColor:
                                        notification.status === "pending" ||
                                        notification.status === "unread"
                                            ? "#fff5f5"
                                            : "#f8fafc",
                                    borderLeft: `4px solid var(--bs-${getTypeColor(
                                        notification.type,
                                    )})`,
                                    borderRadius: "8px",
                                }}
                            >
                                <div
                                    className="me-3 text-muted"
                                    style={{ flexShrink: 0 }}
                                >
                                    <i
                                        className={`bi ${
                                            notification.status === "pending" ||
                                            notification.status === "unread"
                                                ? "bi-circle-fill"
                                                : "bi-circle"
                                        }`}
                                        style={{
                                            fontSize: "1.25rem",
                                            color:
                                                notification.status ===
                                                    "pending" ||
                                                notification.status === "unread"
                                                    ? "var(--bs-primary)"
                                                    : `var(--bs-${getTypeColor(notification.type)})`,
                                        }}
                                    ></i>
                                </div>
                                <div
                                    className="flex-grow-1"
                                    style={{
                                        minWidth: 0,
                                        display: "flex",
                                        flexDirection: "column",
                                    }}
                                >
                                    <div className="d-flex align-items-center gap-2 mb-2 flex-wrap justify-content-between">
                                        <div className="d-flex align-items-center gap-2">
                                            {notification.status ===
                                                "pending" ||
                                            notification.status === "unread" ? (
                                                <strong
                                                    className="text-dark"
                                                    title={
                                                        notification.title ||
                                                        "Notification"
                                                    }
                                                >
                                                    {truncateTitle(
                                                        notification.title,
                                                    )}
                                                </strong>
                                            ) : (
                                                <span
                                                    className="text-secondary"
                                                    title={
                                                        notification.title ||
                                                        "Notification"
                                                    }
                                                >
                                                    {truncateTitle(
                                                        notification.title,
                                                    )}
                                                </span>
                                            )}
                                        </div>
                                        <div
                                            className="d-flex align-items-center gap-1"
                                            style={{ flexShrink: 0 }}
                                        >
                                            {hoveredNotificationId ===
                                                notification.id && (
                                                <div className="d-flex gap-1">
                                                    {notification.status !==
                                                        "read" && (
                                                        <button
                                                            className="btn btn-sm p-0"
                                                            style={{
                                                                border: "none",
                                                                background:
                                                                    "none",
                                                                cursor: "pointer",
                                                                transition:
                                                                    "color 0.2s ease",
                                                                color:
                                                                    hoveredActionButton ===
                                                                    `${notification.id}-read`
                                                                        ? "var(--bs-primary)"
                                                                        : "var(--bs-secondary)",
                                                            }}
                                                            onMouseEnter={() =>
                                                                setHoveredActionButton(
                                                                    `${notification.id}-read`,
                                                                )
                                                            }
                                                            onMouseLeave={() =>
                                                                setHoveredActionButton(
                                                                    null,
                                                                )
                                                            }
                                                            onClick={() =>
                                                                handleMarkSingleAsRead(
                                                                    notification.id,
                                                                )
                                                            }
                                                            title="Mark as read"
                                                        >
                                                            <i className="bi bi-eye-fill"></i>
                                                        </button>
                                                    )}
                                                    {notification.status !==
                                                        "pending" &&
                                                        notification.status !==
                                                            "unread" && (
                                                            <button
                                                                className="btn btn-sm p-0"
                                                                style={{
                                                                    border: "none",
                                                                    background:
                                                                        "none",
                                                                    cursor: "pointer",
                                                                    transition:
                                                                        "color 0.2s ease",
                                                                    color:
                                                                        hoveredActionButton ===
                                                                        `${notification.id}-unread`
                                                                            ? "var(--bs-primary)"
                                                                            : "var(--bs-secondary)",
                                                                }}
                                                                onMouseEnter={() =>
                                                                    setHoveredActionButton(
                                                                        `${notification.id}-unread`,
                                                                    )
                                                                }
                                                                onMouseLeave={() =>
                                                                    setHoveredActionButton(
                                                                        null,
                                                                    )
                                                                }
                                                                onClick={() =>
                                                                    handleMarkSingleAsUnread(
                                                                        notification.id,
                                                                    )
                                                                }
                                                                title="Mark as unread"
                                                            >
                                                                <i className="bi bi-eye-slash-fill"></i>
                                                            </button>
                                                        )}
                                                    <button
                                                        className="btn btn-sm p-0 ms-1"
                                                        style={{
                                                            border: "none",
                                                            background:
                                                                "none",
                                                            cursor: "pointer",
                                                            transition:
                                                                "color 0.2s ease",
                                                            color:
                                                                hoveredActionButton ===
                                                                `${notification.id}-clear`
                                                                    ? "var(--bs-dark)"
                                                                    : "var(--bs-secondary)",
                                                        }}
                                                        onMouseEnter={() =>
                                                            setHoveredActionButton(
                                                                `${notification.id}-clear`,
                                                            )
                                                        }
                                                        onMouseLeave={() =>
                                                            setHoveredActionButton(
                                                                null,
                                                            )
                                                        }
                                                        onClick={() =>
                                                            handleClearSingleNotification(
                                                                notification.id,
                                                            )
                                                        }
                                                        title="Clear notification"
                                                    >
                                                        <i className="bi bi-check-circle-fill"></i>
                                                    </button>
                                                </div>
                                            )}
                                            <button
                                                className="btn btn-sm p-0 text-dark"
                                                style={{
                                                    border: "none",
                                                    background: "none",
                                                    cursor: "pointer",
                                                }}
                                                onClick={() =>
                                                    toggleExpanded(
                                                        notification.id,
                                                    )
                                                }
                                                title="Toggle notification details"
                                            >
                                                <i
                                                    className={`bi ${
                                                        expandedIds.has(
                                                            notification.id,
                                                        )
                                                            ? "bi-arrow-bar-up"
                                                            : "bi-arrow-bar-down"
                                                    }`}
                                                ></i>
                                            </button>
                                        </div>
                                    </div>
                                    <p
                                        className={`mb-2 text-start notification-body ${
                                            !expandedIds.has(notification.id)
                                                ? "collapsed"
                                                : ""
                                        } ${
                                            notification.status === "read"
                                                ? "text-secondary"
                                                : "text-dark"
                                        }`}
                                        style={{
                                            fontSize: "0.95rem",
                                            lineHeight: "1.4",
                                        }}
                                    >
                                        <small>{notification.body}</small>
                                    </p>
                                    {notification.created_at && (
                                        <div
                                            className="d-flex align-items-center justify-content-between"
                                            style={{
                                                marginTop: "auto",
                                                gap: "0.5rem",
                                            }}
                                        >
                                            <button
                                                className="btn btn-sm btn-link p-0 fw-bold text-decoration-none"
                                                onClick={() =>
                                                    handleNotificationAction(
                                                        notification,
                                                    )
                                                }
                                            >
                                                {notificationActionLabel(
                                                    notification,
                                                )}
                                            </button>
                                            <small
                                                className="text-muted"
                                                style={{
                                                    fontSize: "0.75rem",
                                                    marginLeft: "auto",
                                                }}
                                            >
                                                {(() => {
                                                    const date = new Date(
                                                        notification.created_at,
                                                    )
                                                    const dateStr =
                                                        date.toLocaleDateString(
                                                            "en-US",
                                                            {
                                                                year: "numeric",
                                                                month: "long",
                                                                day: "numeric",
                                                            },
                                                        )
                                                    const timeStr =
                                                        date.toLocaleTimeString(
                                                            "en-US",
                                                        )
                                                    return `${dateStr} ${timeStr}`
                                                })()}
                                            </small>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Modal Footer */}
            <div
                className="modal-footer border-top"
                style={{
                    padding: "1.5rem",
                    backgroundColor: "#f8fafc",
                    borderRadius: "0 0 12px 12px",
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "0.75rem",
                }}
            >
                <div className="d-flex gap-2">
                    <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={handleMarkAllAsRead}
                        disabled={
                            isProcessing || sortedNotifications.length === 0
                        }
                        title="Mark all notifications as read"
                    >
                        Mark all as read
                    </button>
                    <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={handleMarkAllAsUnread}
                        disabled={
                            isProcessing || sortedNotifications.length === 0
                        }
                        title="Mark all notifications as unread"
                    >
                        Mark all as unread
                    </button>
                    <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={handleClearAll}
                        disabled={
                            isProcessing || sortedNotifications.length === 0
                        }
                        title="Clear all notifications"
                    >
                        Clear all
                    </button>
                </div>
                <button className="btn btn-outline-secondary" onClick={onClose}>
                    Close
                </button>
            </div>
        </dialog>
    )
}
